import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, Book } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { glossaryData } from "@/data/glossaryData";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import ChatOption from "@/components/ChatOption";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const Glossary = () => {
  const isMobile = useIsMobile();
  const [selectedLetter, setSelectedLetter] = useState<string | null>("A");
  const [searchQuery, setSearchQuery] = useState("");

  const allEntries = Object.values(glossaryData).flat();

  const filteredEntries = searchQuery
    ? allEntries.filter(
        (entry) =>
          entry.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entry.definition.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : selectedLetter
    ? glossaryData[selectedLetter] || []
    : allEntries;

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(selectedLetter === letter ? null : letter);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F0FB] to-white">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/knowledge-hub">
            <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#9b87f5]/10 hover:text-[#9b87f5]">
              <ArrowLeft className="h-4 w-4" />
              Back to Knowledge Hub
            </Button>
          </Link>
        </div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center mb-4">
              <Book className="h-10 w-10 text-corporate-primary-75 mr-3" />
              <h1 className="text-4xl md:text-5xl font-bold text-[#4A607B] relative">
                Crypto Glossary A–Z
                <div className="absolute -bottom-3 left-0 w-full h-1 bg-corporate-secondary-100 rounded-full"></div>
              </h1>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <div className="relative max-w-md mx-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search for terms or definitions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border border-gray-200 focus:border-corporate-primary-75 focus:ring-corporate-primary-50 transition-all duration-300"
              />
            </div>
          </motion.div>

          {!searchQuery && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-md mb-8 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 h-1"></div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 justify-center">
                  {alphabet.map((letter) => (
                    <Button
                      key={letter}
                      variant={selectedLetter === letter ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleLetterClick(letter)}
                      className={`min-w-[2.5rem] ${
                        !glossaryData[letter]?.length
                          ? "opacity-50 cursor-not-allowed"
                          : selectedLetter === letter
                          ? "bg-corporate-primary-75 hover:bg-corporate-primary-100 text-white"
                          : "hover:bg-corporate-primary-25 hover:text-corporate-primary-100 border-corporate-primary-25"
                      }`}
                      disabled={!glossaryData[letter]?.length}
                    >
                      {letter}
                    </Button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-xl shadow-md overflow-hidden"
          >
            <div className="bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 h-1"></div>
            <div className="p-8">
              {!searchQuery && selectedLetter && (
                <h2 className="text-3xl font-bold mb-8 text-corporate-primary-100 border-b border-gray-100 pb-4 flex items-center">
                  <span className="bg-corporate-primary-25 w-12 h-12 rounded-full flex items-center justify-center mr-4 text-corporate-primary-100">
                    {selectedLetter}
                  </span>
                  <span>Terms</span>
                </h2>
              )}
              <div className="space-y-8">
                {filteredEntries.map((entry, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    className="group hover:bg-[#F8F9FE] p-5 -mx-4 rounded-lg transition-colors duration-200 border-l-2 border-transparent hover:border-corporate-primary-75"
                  >
                    <h3 className="text-xl font-semibold text-[#4A607B] mb-3 group-hover:text-corporate-primary-100">
                      {entry.term}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {entry.definition}
                    </p>
                  </motion.div>
                ))}
                {filteredEntries.length === 0 && (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center p-3 bg-gray-100 rounded-full mb-4">
                      <Search className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-gray-500">
                      {searchQuery
                        ? "No entries found for your search."
                        : `No entries found for the letter ${selectedLetter}.`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
      {!isMobile && <ChatOption />}
    </div>
  );
};

export default Glossary;
